
export interface Game {
  id: number;
  title: string;
  slug: string;
  duration: string;
  description: string;
  url: string;
  isCustom: boolean;
}

// Custom games that we implement natively
const customGames = [
  {
    id: "custom/blockstack",
    title: "Block Stack",
    slug: "block-stack",
    description: "Arrange falling blocks to clear lines and improve focus",
    isCustom: true
  },
  {
    id: "custom/asteroids",
    title: "Asteroid Field",
    slug: "asteroid-field",
    description: "Navigate through challenges with focus and determination",
    isCustom: true
  },
  {
    id: "custom/breathing",
    title: "Breathing Exercise",
    slug: "breathing-exercise",
    description: "Focus on the animations to guide your breathing rhythm",
    isCustom: true
  },
  {
    id: "custom/matching",
    title: "Match & Meditate",
    slug: "match-and-meditate",
    description: "Find matching pairs while practicing mindful focus",
    isCustom: true
  }
];

// Helper function to generate slug from title
const generateSlug = (title: string): string => {
  return title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};

// Helper function to shuffle array
const shuffleArray = <T>(array: T[]): T[] => {
  const newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

// Generate a random duration between 3-5 minutes
const getRandomDuration = (): string => {
  return `${Math.floor(Math.random() * 3) + 3} mins`;
};

// Format all games with proper IDs and format
const getGamesList = (): Game[] => {
  // Select all custom games
  const selectedCustomGames = customGames.map((game, index) => {
    return {
      id: index + 1, // Start IDs at 1
      title: game.title,
      slug: game.slug || generateSlug(game.title),
      duration: getRandomDuration(),
      description: game.description,
      url: "", // Custom games don't need URLs
      isCustom: true
    };
  });
  
  // Return the sorted list of games
  return selectedCustomGames.sort((a, b) => a.id - b.id);
};

// Generate the games array - this will refresh whenever the app reloads
export const games: Game[] = getGamesList();

// Utility function to find game by slug or ID
export const findGameBySlugOrId = (slugOrId: string): Game | undefined => {
  // First try to find by slug
  const gameBySlug = games.find(game => game.slug === slugOrId);
  if (gameBySlug) return gameBySlug;
  
  // If not found, try to find by ID
  const id = parseInt(slugOrId, 10);
  if (!isNaN(id)) {
    return games.find(game => game.id === id);
  }
  
  return undefined;
};
